import { observable, action, toJS, useStrict } from 'mobx';
// useStrict(true);

var getStackTrace = function () {
  var obj = {};
  Error.captureStackTrace(obj, getStackTrace);
  return obj.stack;
};


export default class BaseStore {
  Model;
  transportLayer;

  @observable items = [];
  @observable isLoading = false;
  @observable isSaving = false;
  @observable isEmpty = false;
  @observable error = {};

  constructor(Model, transportLayer) {
    this.transportLayer = transportLayer;
    this.Model = Model;
  }

  asJson(item) {
    const json = item.asJson ? item.asJson : toJS(item);
    if (json.store) { delete json.store; }
    return json;
  }

  _create(item) {
    const json = this.asJson(item);
    delete json.id;
    return this.transportLayer.create(json);
  }

  _update(id, item) {
    if (!id) { throw new Error('Update failure. Invalid id.'); }
    return this.transportLayer.update(id, this.asJson(item));
  }

  _remove(id) {
    if (!id) { throw new Error('Delete failure. Invalid id.'); }
    return this.transportLayer.remove(id);
  }

  _removeMultiple(ids) {
    return this.transportLayer.removeMultiple(ids);
  }

  _fetchAll() {
    return this.transportLayer.fetchAll();
  }

  _fetchById(id) {
    return this.transportLayer.fetchById(id);
  }

  @action
  fetchItems() {
    if (this.isLoading) return Promise.resolve(this.items);
    this.isLoading = true;

    const handleFetchAllSuccess = fetchedItems => {
      fetchedItems.forEach(json => {
        this.updateItemFromServer(json);
      });
      this.isLoading = false;
      this.isEmpty = !fetchedItems.length;
      return fetchedItems;
    };

    return this
      ._fetchAll()
      .then(action(handleFetchAllSuccess))
      .catch(action(err => {
        console.log('err', err);
        this.isLoading = false;
        this.isEmpty = true;
      }));
  }

  @action resolveItem(json) {
    let item = this.findById(json.id);
    if (!item) {
      item = new this.Model(this, json);
      this.items.push(item);
    } else {
      item.updateFromJson(json);
    }
    return item;
  }

  @action
  fetchItem(itemId, refresh) {
    this.isLoading = true;

    if (!refresh) {
      const foundItem = this.findById(itemId);
      if (foundItem) {
        this.isLoading = false;
        return;
      }
    }
    const handleFetchByIdSuccess = json => {
      this.updateItemFromServer(json);
      this.isLoading = false;
      return json;
    };

    return this
      ._fetchById(itemId)
      .then(action(handleFetchByIdSuccess))
      .catch(action(err => {
        console.log('err', err);

        this.isLoading = false;
      }));
  }

  @action fetchField(itemId, field) {
    if (this.isLoading) { return; } // TODO: Figure out better way of throttling
    const foundItem = this.findById(itemId);
    if (foundItem && foundItem[field]) { return; }
    this.fetchItem(itemId, true);
  }

  // deprecated: use updateItemFromServer instead
  @action updateItemFieldFromServer(id, field, json) {
    if (!id) { return; }
    const item = this.items.find(el => +el.id === +id);
    item.updateFieldFromJson(field, json);
  }

  @action updateItemFromServer(json, field, id) {
    const itemId = id || json.id;

    if (!itemId) { return; }
    let item = this.items.find(el => +el.id === +itemId);

    // if field is specified, update only that field and return. #unstable
    // if (item && field) {
    // item.updateFromJson(json, field);
    // return;
    // }

    if (!item) {
      item = new this.Model(this, json);
      if (!this.items) {
        this.items = [];
      }
      this.items.push(item);
      return;
    }

    item.updateFromJson(json, field);
  }

  @action removeItemFromServer(json, field, id) {
    const itemId = id;

    if (!itemId) { return; }
    const item = this.items.find(el => +el.id === +itemId);

    if (field) {
      if (Array.isArray(json.ids)) {
        json.ids.forEach(deletableId => {
          const foundFieldItem = item[field].find(fieldItem => +fieldItem.id === +deletableId);
          item[field].remove(foundFieldItem);
        });
      } else {
        const deletableId = json.ids;
        const foundFieldItem = item[field].find(fieldItem => +fieldItem.id === +deletableId);
        item[field].remove(foundFieldItem);
      }
      return;
    }

    this.items.remove(item);
  }

  findById(itemId, key) {
    const item = this.items.find(item => +item.id === +itemId);
    if (!key) return item;
    return !!item ? item[key] : '';
  }

  createItem(json) {
    this.isSaving = true;
    return this._create(json)
      .then(action((row) => {
        this.isSaving = false;
        // return this.fetchItems().then(() => row);
        const item = new this.Model(this, { ...json, id: +row.id });
        if (item.id) { this.items.push(item); }
        return item;
      })).catch(err => {
        this.isSaving = false;
        return Promise.reject(err);
      });
  }

  updateItem(json) {
    const item = this.findById(json.id);
    this.isSaving = true;
    return this
      ._update(json.id, json)
      .then(action(() => {
        if (!!item) item.updateFromJson(json);
        // return this.fetchItems();
        this.isSaving = false;
      }))
      .catch(action(err => {
        this.isSaving = false;
        throw new Error(err.message);
      }));
  }

  @action removeItem(id) {
    this.isSaving = true;
    const item = this.findById(id);
    return this
      ._remove(id)
      .then(action(() => {
        this.items.remove(item);
        this.isSaving = false;
        return ({ removed: true })
      }))
      .catch(action(err => {
        console.log('ERROR', err);
        this.isSaving = false;
        throw new Error(err.message);
      }));
  }

  @action removeItems(ids) {
    if (typeof ids === 'number' || typeof ids === 'string') {
      return this.removeItem(ids);
      // return;
    }

    this.isSaving = true;
    return this
      ._removeMultiple(ids)
      .then(action(() => {
        ids.forEach(id => {
          const item = this.items.find(el => +el.id === +id);
          this.items.remove(item);
        });
        this.isSaving = false;
      }))
      .catch(action(err => {
        console.log('ERROR', err);
        this.isSaving = false;
        throw new Error((err && err.message) || err);
      }));
  }
}




// WEBPACK FOOTER //
// ./src/stores/BaseStore.js